/* Enterprise sales inquiry (the /enterprise "Contact Enterprise Sales" popup and
   /enterprise/get-started), sent to GoHighLevel through the "Enterprise Lead" workflow's
   Inbound Webhook trigger, which creates/updates the contact and runs the automations.
   Same pattern as src/Component/TuroAlternative/transitionLead.js: the webhook takes browser
   POSTs directly and needs no API key (a key could not be kept secret in this bundle).

   Keys are snake_case and lists are comma-joined to match the field mapping in the GHL
   workflow; `email` must stay mapped to the standard contact Email field, which is how GHL
   merges repeat submissions onto the same contact. Renaming a key here breaks that mapping. */

const WEBHOOK_URL =
  "https://services.leadconnectorhq.com/hooks/5iYYPbRZ4Q8RrtEgZ77y/webhook-trigger/8f7fcabd-b453-4aea-bbd2-3ff1975b42a3";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/* Spam check: nobody fills this form in faster than this. */
const MIN_FILL_MS = 3000;
const MAX = 200;
const MAX_MESSAGE = 4000;

const clean = (value, max = MAX) => (typeof value === "string" ? value.trim().slice(0, max) : "");
const cleanList = (value) =>
  Array.isArray(value) ? value.filter((v) => typeof v === "string").map((v) => v.slice(0, MAX)).slice(0, 30) : [];

export async function submitEnterpriseLead(lead) {
  /* Bots fill the hidden honeypot field or submit instantly. Report success so they move on,
     but never post, so the GHL workflow doesn't fire. */
  if (lead.honeypot || Date.now() - (lead.startedAt || 0) < MIN_FILL_MS) return { ok: true };

  const record = {
    firstName: clean(lead.firstName),
    lastName: clean(lead.lastName),
    businessName: clean(lead.businessName),
    email: clean(lead.email).toLowerCase(),
    phone: clean(lead.phone, 40),
    website: clean(lead.website),
    states: clean(lead.states),
    fleetSize: clean(lead.fleetSize, 40),
    referralSource: clean(lead.referralSource),
    businessTypes: cleanList(lead.businessTypes),
    products: cleanList(lead.products),
    message: clean(lead.message, MAX_MESSAGE),
  };

  if (!record.firstName || !record.lastName) return { ok: false, error: "Please enter your full name." };
  if (!record.businessName) return { ok: false, error: "Please enter your business name." };
  if (!EMAIL.test(record.email)) return { ok: false, error: "Please enter a valid work email." };
  if (!record.states) return { ok: false, error: "Please list the state(s) you operate in." };
  if (!record.fleetSize) return { ok: false, error: "Please enter your fleet size." };
  if (!record.referralSource) return { ok: false, error: "Please tell us how you heard about us." };
  if (record.businessTypes.length === 0) return { ok: false, error: "Please select at least one business type." };
  if (record.products.length === 0) return { ok: false, error: "Please select at least one product." };

  const payload = {
    first_name: record.firstName,
    last_name: record.lastName,
    email: record.email,
    phone: record.phone,
    company_name: record.businessName,
    website: record.website,
    states_of_operation: record.states,
    fleet_size: record.fleetSize,
    referral_source: record.referralSource,
    business_types: record.businessTypes.join(", "),
    products_interested: record.products.join(", "),
    message: record.message,
    contact_consent: lead.consent ? "yes" : "no",
    source: "rentid-enterprise",
    page: typeof window !== "undefined" ? window.location.pathname : "",
    submitted_at: new Date().toISOString(),
  };

  const res = await fetch(WEBHOOK_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Enterprise lead webhook responded ${res.status}`);
  return { ok: true };
}
